/**
 * ProfilePage — public profile at /u/:handle. Shows display name, bio,
 * variant-aware stats, ELO, recent matches with variant badges and
 * replay links.
 */

import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { apiPath } from '../api.js';

type ProfileResponse = {
  ok: true;
  profile: {
    user_id: string;
    handle: string;
    display_name: string;
    avatar_url: string | null;
    bio: string | null;
    created_at: number;
    last_seen_at: number | null;
    online: boolean;
  };
  stats: {
    by_variant: readonly { variant: string; matches: number; wins: number; losses: number }[];
    elo: number | null;
  };
};

type MatchSummary = {
  match_ulid: string;
  variant: string;
  p1: string;
  p2: string;
  winner: string | null;
  end_reason: string | null;
  ended_at: number | null;
};

const variantBadge = (variant: string) =>
  ({
    casual: 'bg-sky-700 text-sky-100',
    ranked: 'bg-amber-700 text-amber-100',
    practice: 'bg-cosmos-700 text-cosmos-200',
  })[variant] ?? 'bg-cosmos-700 text-cosmos-200';

export function ProfilePage({ handle }: { handle: string }): ReactElement {
  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  const [matches, setMatches] = useState<readonly MatchSummary[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(apiPath(`/api/users/${encodeURIComponent(handle)}`), {
          credentials: 'include',
        });
        if (!r.ok) throw new Error(`profile ${r.status}`);
        const json = (await r.json()) as ProfileResponse;
        if (cancelled) return;
        setProfile(json);

        const r2 = await fetch(
          apiPath(`/api/u/${encodeURIComponent(json.profile.user_id)}/matches?limit=20`),
          { credentials: 'include' },
        );
        if (r2.ok) {
          const j2 = (await r2.json()) as { ok: true; matches: MatchSummary[] };
          if (!cancelled) setMatches(j2.matches);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [handle]);

  if (error) {
    return <div className="p-6 text-rose-400">Profile error: {error}</div>;
  }
  if (!profile) {
    return <div className="p-6 text-cosmos-400">Loading profile…</div>;
  }

  const totals = profile.stats.by_variant.reduce(
    (acc, v) => ({ matches: acc.matches + v.matches, wins: acc.wins + v.wins }),
    { matches: 0, wins: 0 },
  );

  return (
    <main className="mx-auto max-w-3xl space-y-6 px-4 py-6 text-cosmos-100">
      <header className="flex items-start gap-4 border-cosmos-800 border-b pb-4">
        {profile.profile.avatar_url ? (
          <img
            src={profile.profile.avatar_url}
            alt={profile.profile.handle}
            className="h-16 w-16 rounded-full border border-cosmos-700"
          />
        ) : (
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-cosmos-800 text-xl">
            {profile.profile.display_name[0]?.toUpperCase()}
          </div>
        )}
        <div className="flex-1">
          <h1 className="text-2xl font-semibold">
            {profile.profile.display_name}{' '}
            <span className="font-mono text-sm text-cosmos-400">@{profile.profile.handle}</span>
          </h1>
          {profile.profile.bio ? (
            <p className="text-sm text-cosmos-300">{profile.profile.bio}</p>
          ) : null}
          <p className="mt-1 text-xs text-cosmos-500">
            <span
              className={`mr-1 inline-block h-2 w-2 rounded-full ${profile.profile.online ? 'bg-emerald-400' : 'bg-cosmos-700'}`}
            />
            {profile.profile.online ? 'online' : 'offline'} · joined{' '}
            {new Date(profile.profile.created_at * 1000).toLocaleDateString()}
          </p>
        </div>
      </header>

      <section>
        <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-cosmos-400">Stats</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Matches" value={totals.matches} />
          <Stat label="Wins" value={totals.wins} />
          <Stat
            label="Win rate"
            value={totals.matches ? `${Math.round((totals.wins / totals.matches) * 100)}%` : '—'}
          />
          <Stat label="ELO" value={profile.stats.elo ?? '—'} />
        </div>
        {profile.stats.by_variant.length > 0 ? (
          <div className="mt-3 grid gap-1 text-xs">
            {profile.stats.by_variant.map((v) => (
              <div key={v.variant} className="flex items-center gap-2">
                <span className={`rounded px-2 py-0.5 ${variantBadge(v.variant)}`}>
                  {v.variant}
                </span>
                <span className="text-cosmos-300">
                  {v.wins}–{v.losses} ({v.matches} matches)
                </span>
              </div>
            ))}
          </div>
        ) : null}
      </section>

      <section>
        <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-cosmos-400">
          Recent matches
        </h2>
        {matches.length === 0 ? (
          <p className="text-xs text-cosmos-500 italic">No completed matches yet.</p>
        ) : (
          <ul className="divide-y divide-cosmos-800">
            {matches.map((m) => (
              <li key={m.match_ulid} className="flex items-center gap-2 py-2 text-sm">
                <span className={`rounded px-2 py-0.5 text-[10px] ${variantBadge(m.variant)}`}>
                  {m.variant}
                </span>
                <span className="font-mono text-xs text-cosmos-400">{m.match_ulid}</span>
                <span className="flex-1 text-cosmos-300">
                  {m.p1} vs {m.p2}
                </span>
                <span
                  className={
                    m.winner === profile.profile.user_id ? 'text-emerald-400' : 'text-rose-400'
                  }
                >
                  {m.winner === null ? '—' : m.winner === profile.profile.user_id ? 'win' : 'loss'}
                </span>
                <a
                  href={`/replay/${m.match_ulid}`}
                  className="text-xs text-amber-400 hover:underline"
                >
                  replay
                </a>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}

function Stat({ label, value }: { label: string; value: string | number }): ReactElement {
  return (
    <div className="rounded border border-cosmos-700 bg-cosmos-900/50 p-3">
      <div className="text-xs uppercase tracking-wide text-cosmos-400">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}
